import { useMemo, useState } from 'react'

import { createMoney, formatMoney } from '@percent/utility'

import { SAVINGS_CALCULATOR_CURRENCY, SavingsPartner } from './savingsCalculator.config'
import { PartnerSavingsResult, PartnerSelection, SavingsTotals } from './SavingsCalculator.types'

export const SAVINGS_CALCULATOR_STEPS = 4

const DEFAULT_SEATS = 5
const MAX_SEATS = 10000

type UseSavingsCalculatorParams = {
  partners: SavingsPartner[]
}

export function useSavingsCalculator({ partners }: UseSavingsCalculatorParams) {
  const [step, setStep] = useState(0)
  const [selection, setSelection] = useState<PartnerSelection>({})

  const results: PartnerSavingsResult[] = useMemo(
    () =>
      Object.entries(selection)
        .map(([id, { seats }]) => {
          const partner = partners.find(item => item.id === id)

          if (!partner) {
            return null
          }

          const current = partner.monthlyPrice * seats * 12
          const after = partner.discountedMonthlyPrice * seats * 12
          const saving = Math.max(0, current - after)

          return { partner, seats, current, saving, after }
        })
        .filter((result): result is PartnerSavingsResult => result !== null)
        .sort((a, b) => b.saving - a.saving),
    [selection, partners]
  )

  const totals: SavingsTotals = useMemo(
    () =>
      results.reduce<SavingsTotals>(
        (acc, { current, saving, after }) => ({
          current: acc.current + current,
          saving: acc.saving + saving,
          after: acc.after + after
        }),
        { current: 0, saving: 0, after: 0 }
      ),
    [results]
  )

  const togglePartner = (id: string) =>
    setSelection(prev => {
      const next = { ...prev }

      if (next[id]) {
        delete next[id]
      } else {
        next[id] = { seats: DEFAULT_SEATS }
      }

      return next
    })

  const setSeats = (id: string, seats: number) =>
    setSelection(prev => ({ ...prev, [id]: { seats: Math.min(Math.max(1, seats), MAX_SEATS) } }))

  const formatAmount = (amount: number) =>
    formatMoney(createMoney(amount, SAVINGS_CALCULATOR_CURRENCY), { hideDecimals: true })

  const goNext = () => setStep(current => Math.min(current + 1, SAVINGS_CALCULATOR_STEPS - 1))
  const goBack = () => setStep(current => Math.max(current - 1, 0))

  return {
    step,
    selection,
    partners,
    results,
    totals,
    selectedCount: Object.keys(selection).length,
    togglePartner,
    setSeats,
    formatAmount,
    goNext,
    goBack
  }
}
